import { useAuthStore } from '../auth/store';
/**
 * File-download helpers for admin exports. Each entity has a thin wrapper that
 * translates the relevant filter shape into a query string and triggers a blob
 * download. Auth uses the same Bearer token as the rest of the app.
 */
import { ApiError } from './client';

const baseUrl = (() => {
  const env = import.meta.env.VITE_API_URL;
  if (env) return env.replace(/\/$/, '');
  if (import.meta.env.DEV) return '/api';
  return '';
})();

interface DownloadOpts {
  path: string;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Filename used when the server doesn't supply Content-Disposition. */
  fallbackFilename: string;
}

/**
 * Trigger a file download. Reads the bearer token from the auth store, fires
 * fetch, parses the server's filename out of Content-Disposition (so we get
 * the timestamped name from the API), then synthesises a click on a hidden
 * anchor to start the browser's save flow.
 */
export async function downloadFile(opts: DownloadOpts): Promise<void> {
  const token = useAuthStore.getState().token;
  const url = buildUrl(opts.path, opts.query);

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      authorization: token ? `Bearer ${token}` : '',
    },
    credentials: 'omit',
  });

  if (res.status === 401) useAuthStore.getState().clear();
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = res.statusText || `HTTP ${res.status}`;
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === 'object' && typeof parsed.error === 'string') {
          message = parsed.error;
        }
      } catch {
        message = text.slice(0, 200);
      }
    }
    throw new ApiError(res.status, message);
  }

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);

  const filename =
    parseFilenameFromContentDisposition(res.headers.get('content-disposition')) ??
    opts.fallbackFilename;

  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so the browser has a moment to start the download.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 5_000);
}

function buildUrl(path: string, query: DownloadOpts['query']): string {
  const fullPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${baseUrl}${fullPath}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return baseUrl.startsWith('http') ? url.toString() : `${url.pathname}${url.search}`;
}

function parseFilenameFromContentDisposition(value: string | null): string | null {
  if (!value) return null;
  // Try filename*=UTF-8''… first (RFC 5987), then plain filename="…".
  const star = value.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^"(.+)"$/, '$1'));
    } catch {
      /* fall through */
    }
  }
  const plain = value.match(/filename="([^"]+)"/i) ?? value.match(/filename=([^;]+)/i);
  return plain?.[1]?.trim().replace(/^"|"$/g, '') ?? null;
}

// ---------- typed wrappers ----------

export type CsvOrJson = 'csv' | 'json';
export type CsvJsonOrIcs = 'csv' | 'json' | 'ics';

export function exportPosts(opts: {
  format: CsvOrJson;
  type?: 'post' | 'page';
  locale: string;
  q?: string;
  category?: string;
  tag?: string;
  author?: string;
  from?: string;
  to?: string;
  sort?: 'newest' | 'oldest';
}): Promise<void> {
  return downloadFile({
    path: '/admin/export/posts',
    query: { ...opts },
    fallbackFilename: `${opts.type ?? 'post'}s-${opts.locale}.${opts.format}`,
  });
}

export function exportEvents(opts: {
  format: CsvJsonOrIcs;
  locale?: string;
  status?: 'draft' | 'published' | 'cancelled';
  from?: string;
  to?: string;
}): Promise<void> {
  return downloadFile({
    path: '/admin/export/events',
    query: { ...opts },
    fallbackFilename: `events.${opts.format}`,
  });
}

export function exportProgrammes(opts: {
  format: CsvOrJson;
  locale?: string;
}): Promise<void> {
  return downloadFile({
    path: '/admin/export/programmes',
    query: { ...opts },
    fallbackFilename: `programmes.${opts.format}`,
  });
}

export function exportProgrammeSlots(slug: string, locale: string): Promise<void> {
  return downloadFile({
    path: `/admin/export/programmes/${encodeURIComponent(slug)}/slots`,
    query: { locale },
    fallbackFilename: `programme-${slug}-slots.csv`,
  });
}

export function exportEpisodes(opts: {
  format: CsvOrJson;
  programmeId: string;
  locale?: string;
  publishedOnly?: boolean;
}): Promise<void> {
  return downloadFile({
    path: '/admin/export/episodes',
    query: {
      format: opts.format,
      programmeId: opts.programmeId,
      locale: opts.locale,
      publishedOnly: opts.publishedOnly ? '1' : undefined,
    },
    fallbackFilename: `episodes.${opts.format}`,
  });
}

export function exportChannels(format: CsvOrJson): Promise<void> {
  return downloadFile({
    path: '/admin/export/channels',
    query: { format },
    fallbackFilename: `channels.${format}`,
  });
}

export function exportUsers(opts: {
  format: CsvOrJson;
  email?: string;
  role?: 'user' | 'admin' | 'correspondent';
}): Promise<void> {
  return downloadFile({
    path: '/admin/export/users',
    query: { ...opts },
    fallbackFilename: `users.${opts.format}`,
  });
}

export function exportPushes(opts: {
  format: CsvOrJson;
  status?: 'pending' | 'sent' | 'cancelled';
  postSlug?: string;
  locale?: string;
  manualOnly?: boolean;
  from?: string;
  to?: string;
}): Promise<void> {
  return downloadFile({
    path: '/admin/export/pushes',
    query: {
      format: opts.format,
      status: opts.status,
      postSlug: opts.postSlug,
      locale: opts.locale,
      manualOnly: opts.manualOnly ? '1' : undefined,
      from: opts.from,
      to: opts.to,
    },
    fallbackFilename: `push-history.${opts.format}`,
  });
}

export function exportSchedule(opts: {
  format: 'csv' | 'ics';
  channel: string;
  locale: string;
  weekStart: string;
}): Promise<void> {
  return downloadFile({
    path: '/admin/export/schedule',
    query: { ...opts },
    fallbackFilename: `schedule-${opts.channel}-${opts.locale}-${opts.weekStart}.${opts.format}`,
  });
}
